import React, { useEffect, useState } from 'react';

import { Box, Chip, Typography, Pagination, Divider, Container } from '@mui/material';

import GeneralButton from '../../../../../../../Components/GeneralButton';
import { convertTimeStamp } from '../../../../../../../utilities/helpers';
import useStyles from './style';

/**
 * @name ViewAllChangeLogs
 * @description Displays all change logs with pagination, allowing the user to view details of specific logs.
 * @param {function} updateTab - Function to update the current tab.
 * @param {function} updateChangeDetails - Function to update the details of the selected change log.
 * @returns {JSX.Element} - The rendered component.
 */
const ViewAllChangeLogs = ({ updateCurrentSelectedChange, filteredAllChangeLogs }) => {
	const classes = useStyles();
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 20;

	const filteredChangeLogs = filteredAllChangeLogs || [];
	const totalPages = Math.ceil(filteredChangeLogs?.length / itemsPerPage);
	const indexOfLastItem = Number(currentPage * itemsPerPage);
	const indexOfFirstItem = Number(indexOfLastItem - itemsPerPage);
	const currentPageLogs = filteredChangeLogs?.slice(indexOfFirstItem, indexOfLastItem);

	/**
	 * @name handlePageChange
	 * @description Handles the change of the pagination page.
	 * @param {object} event - The event object.
	 * @param {number} value - The new page number.
	 */
	const handlePageChange = (event, value) => {
		setCurrentPage(value);
	};

	const getObjKeys = (obj) => {
		return Object.keys(obj);
	};

	/**
	 * @name handleViewDetails
	 * @description Handles the action of viewing details for a specific change log.
	 * @param {object} changes - The changes object of the log.
	 */
	const handleViewDetails = (changes) => {
		updateCurrentSelectedChange(changes);
	};

	/**
	 * @description Resets the current page to 1 whenever the `changeLogs` array changes.
	 * This ensures that the user is redirected to the first page of logs when new logs are loaded or existing logs are updated.
	 */
	useEffect(() => {
		setCurrentPage(1);
	}, [filteredAllChangeLogs]);

	return (
		currentPageLogs &&
		currentPageLogs.length > 0 && (
			<Box
				component="section"
				className={classes.sectionContainer}
				sx={{ width: '94%', margin: '3rem auto 0 auto' }}
			>
				{currentPageLogs?.map((item, index) => (
					<Box key={index} className={classes.logContainer}>
						<Box
							display="flex"
							flexDirection="row"
							flexWrap="wrap"
							gap={{ xs: 2, sm: 2 }}
							sx={{ width: '100%' }}
						>
							<Typography
								fontWeight={'bold'}
								variant="h6"
								sx={{ fontWeight: 'bold', color: 'teal' }}
								className={classes.changeType}
							>
								{getObjKeys(item.changes).length === 0 && <span>No Changes</span>}
							</Typography>

							<Typography
								fontWeight={'bold'}
								variant="h6"
								sx={{ fontWeight: 'bold', color: 'teal' }}
								className={classes.changeType}
							>
								{getObjKeys(item.changes).length > 1
									? 'Multiple Changes'
									: getObjKeys(item.changes)[0]}
							</Typography>

							<Divider orientation="vertical" flexItem sx={{ borderColor: 'black' }} />

							<Typography
								fontWeight={'bold'}
								sx={{ fontWeight: 'bold' }}
								className={classes.changeType}
							>
								Date : {convertTimeStamp(item?.timestamp).date}
							</Typography>
							<Divider orientation="vertical" flexItem sx={{ borderColor: 'black' }} />

							<Typography fontWeight={'bold'} sx={{ fontWeight: 'bold', color: '#0c4a6e' }}>
								{`Time : ${convertTimeStamp(item?.timestamp).time}`}
							</Typography>

							<Divider orientation="vertical" flexItem sx={{ borderColor: 'black' }} />

							<Chip
								label={item.action.name}
								size="small"
								sx={{ backgroundColor: '#93c5fd', color: 'black', fontWeight: 'bold' }}
							/>

							<Chip
								label={item?.userId}
								size="small"
								sx={{ backgroundColor: '#bae6fd', color: 'black' }}
							/>
						</Box>

						{getObjKeys(item.changes).length > 0 && (
							<Box
								display="flex"
								flexDirection="row"
								justifyContent="space-between"
								sx={{ marginTop: '1.5rem' }}
							>
								{getObjKeys(item.changes).length > 1 && (
									<Box className={classes.changeKeysContainer}>
										{getObjKeys(item.changes).map((changeKey, index) => (
											<Chip
												key={index}
												label={changeKey}
												size="small"
												sx={{
													backgroundColor: '#d1d5db',
													margin: '.4rem',
													display: 'inline-block'
												}}
											/>
										))}
									</Box>
								)}

								<GeneralButton
									variant="contained"
									fontWeight={400}
									sx={{
										height: '3.6rem',
										marginLeft: getObjKeys(item.changes).length === 1 ? 'auto' : '1rem'
									}}
									name="ELASTIC_SEARCH_FETCH_DETAILS"
									onClick={() => {
										handleViewDetails(item);
									}}
								>
									Show Details
								</GeneralButton>
							</Box>
						)}
					</Box>
				))}

				<Box className={classes.paginationContainer}>
					<Pagination
						count={totalPages}
						page={currentPage}
						onChange={handlePageChange}
						color="secondary"
					/>
				</Box>
			</Box>
		)
	);
};

export default ViewAllChangeLogs;
